import React, { useState, useRef, useEffect } from 'react';
import { FaSort, FaSortAmountDown, FaSortAmountUp, FaChevronDown } from 'react-icons/fa';
import { useLanguage } from '../../App';
import './SortOptions.css';

const SortOptions = ({
  type = 'projects', // 'projects' or 'units'
  sortBy = 'created_at',
  sortOrder = 'desc',
  onSortChange,
  className = ''
}) => {
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const projectSortOptions = [
    {
      value: 'created_at',
      label: language === 'ar' ? 'الأحدث أولاً' : 'Newest First',
      order: 'desc'
    },
    {
      value: 'created_at',
      label: language === 'ar' ? 'الأقدم أولاً' : 'Oldest First',
      order: 'asc'
    },
    {
      value: 'min_price',
      label: language === 'ar' ? 'السعر: من الأقل للأعلى' : 'Price: Low to High',
      order: 'asc'
    },
    {
      value: 'max_price',
      label: language === 'ar' ? 'السعر: من الأعلى للأقل' : 'Price: High to Low',
      order: 'desc'
    },
    {
      value: 'name',
      label: language === 'ar' ? 'الاسم: أ-ي' : 'Name: A-Z',
      order: 'asc'
    },
    {
      value: 'name',
      label: language === 'ar' ? 'الاسم: ي-أ' : 'Name: Z-A',
      order: 'desc'
    },
    {
      value: 'launch_date',
      label: language === 'ar' ? 'تاريخ الإطلاق: الأحدث' : 'Launch Date: Newest',
      order: 'desc'
    },
    {
      value: 'launch_date',
      label: language === 'ar' ? 'تاريخ الإطلاق: الأقدم' : 'Launch Date: Oldest',
      order: 'asc'
    },
    {
      value: 'completion_date',
      label: language === 'ar' ? 'تاريخ الإنجاز: الأقرب' : 'Completion: Nearest',
      order: 'asc'
    },
    {
      value: 'completion_date',
      label: language === 'ar' ? 'تاريخ الإنجاز: الأبعد' : 'Completion: Farthest',
      order: 'desc'
    },
    {
      value: 'construction_progress',
      label: language === 'ar' ? 'نسبة الإنجاز: الأعلى' : 'Progress: Highest',
      order: 'desc'
    },
    {
      value: 'construction_progress',
      label: language === 'ar' ? 'نسبة الإنجاز: الأقل' : 'Progress: Lowest',
      order: 'asc'
    },
    {
      value: 'total_units',
      label: language === 'ar' ? 'عدد الوحدات: الأكثر' : 'Units: Most',
      order: 'desc'
    },
    {
      value: 'total_units',
      label: language === 'ar' ? 'عدد الوحدات: الأقل' : 'Units: Least',
      order: 'asc'
    },
    {
      value: 'developer_name',
      label: language === 'ar' ? 'المطور: أ-ي' : 'Developer: A-Z',
      order: 'asc'
    },
    {
      value: 'developer_name',
      label: language === 'ar' ? 'المطور: ي-أ' : 'Developer: Z-A',
      order: 'desc'
    }
  ];

  const unitSortOptions = [
    {
      value: 'created_at',
      label: language === 'ar' ? 'الأحدث أولاً' : 'Newest First',
      order: 'desc'
    },
    {
      value: 'price_with_tax',
      label: language === 'ar' ? 'السعر: من الأقل للأعلى' : 'Price: Low to High',
      order: 'asc'
    },
    {
      value: 'price_with_tax',
      label: language === 'ar' ? 'السعر: من الأعلى للأقل' : 'Price: High to Low',
      order: 'desc'
    },
    {
      value: 'area',
      label: language === 'ar' ? 'المساحة: من الأصغر للأكبر' : 'Area: Small to Large',
      order: 'asc'
    },
    {
      value: 'area',
      label: language === 'ar' ? 'المساحة: من الأكبر للأصغر' : 'Area: Large to Small',
      order: 'desc'
    },
    {
      value: 'price_per_sqm',
      label: language === 'ar' ? 'سعر المتر: من الأقل للأعلى' : 'Price per SQM: Low to High',
      order: 'asc'
    },
    {
      value: 'price_per_sqm',
      label: language === 'ar' ? 'سعر المتر: من الأعلى للأقل' : 'Price per SQM: High to Low',
      order: 'desc'
    },
    {
      value: 'parcel_number',
      label: language === 'ar' ? 'رقم الوحدة: تصاعدي' : 'Unit Number: Ascending',
      order: 'asc'
    },
    {
      value: 'parcel_number',
      label: language === 'ar' ? 'رقم الوحدة: تنازلي' : 'Unit Number: Descending',
      order: 'desc'
    },
    {
      value: 'delivery_date',
      label: language === 'ar' ? 'تاريخ التسليم: الأقرب' : 'Delivery: Nearest',
      order: 'asc'
    },
    {
      value: 'delivery_date',
      label: language === 'ar' ? 'تاريخ التسليم: الأبعد' : 'Delivery: Farthest',
      order: 'desc'
    },
    {
      value: 'sales_status',
      label: language === 'ar' ? 'الحالة: متاح أولاً' : 'Status: Available First',
      order: 'asc'
    },
    {
      value: 'expected_roi',
      label: language === 'ar' ? 'العائد المتوقع: الأعلى' : 'Expected ROI: Highest',
      order: 'desc'
    },
    {
      value: 'expected_roi',
      label: language === 'ar' ? 'العائد المتوقع: الأقل' : 'Expected ROI: Lowest',
      order: 'asc'
    }
  ];

  const sortOptions = type === 'projects' ? projectSortOptions : unitSortOptions;

  const getCurrentSortLabel = () => {
    const currentOption = sortOptions.find(
      option => option.value === sortBy && option.order === sortOrder
    );
    return currentOption ? currentOption.label : (language === 'ar' ? 'الأحدث أولاً' : 'Newest First');
  };

  const handleSortSelect = (option) => {
    if (onSortChange) {
      onSortChange(option.value, option.order);
    }
    setIsOpen(false);
  };

  const getSortIcon = () => {
    if (sortOrder === 'asc') {
      return <FaSortAmountUp className="sort-icon" />;
    } else {
      return <FaSortAmountDown className="sort-icon" />;
    }
  };

  return (
    <div className={`sort-options ${language === 'ar' ? 'rtl' : 'ltr'} ${className}`} ref={dropdownRef}>
      <button
        className="sort-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className="sort-trigger-content">
          <FaSort className="sort-main-icon" />
          <span className="sort-label">
            {language === 'ar' ? 'ترتيب:' : 'Sort:'}
          </span>
          <span className="sort-current">
            {getCurrentSortLabel()}
          </span>
          {getSortIcon()}
        </div>
        <FaChevronDown className={`dropdown-arrow ${isOpen ? 'open' : ''}`} />
      </button>

      {isOpen && (
        <div className="sort-dropdown">
          <div className="sort-dropdown-header">
            <span>{language === 'ar' ? 'ترتيب النتائج' : 'Sort Results'}</span>
          </div>
          <ul className="sort-options-list" role="listbox">
            {sortOptions.map((option, index) => {
              const isSelected = option.value === sortBy && option.order === sortOrder;
              return (
                <li
                  key={`${option.value}-${option.order}-${index}`}
                  className={`sort-option ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleSortSelect(option)}
                  role="option"
                  aria-selected={isSelected}
                >
                  <span className="option-label">{option.label}</span>
                  {isSelected && (
                    <div className="selected-indicator">
                      {option.order === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SortOptions;
